import { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TareaItem from "./TareaItem";
import NuevaTareaForm from "./NuevaTareaForm";
import KpiCard from "./ui/KpiCard";
import SectionHeader from "./ui/SectionHeader";
import { canalLabel } from "./pipelineUtils";

export default function TareasFollowup({ store }) {
  const [openNueva, setOpenNueva] = useState(false);
  const [filtro, setFiltro] = useState("pendientes");
  const [filtroTipo, setFiltroTipo] = useState("");

  const getLeadNombre = (leadId) => store.leads.find((l) => l.id === leadId)?.nombre || "";

  const hoy = new Date().toISOString().slice(0, 10);
  const tareas = store.tareasFollowup
    .filter((t) => {
      if (filtro === "canceladas") return Boolean(t.cancelada);
      if (t.cancelada) return filtro === "todas";
      if (filtro === "pendientes") return !t.completada;
      if (filtro === "vencidas") return !t.completada && t.fechaProgramada < hoy;
      if (filtro === "completadas") return t.completada;
      return true;
    })
    .filter((t) => {
      if (filtroTipo && t.tipo !== filtroTipo) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.completada !== b.completada) return a.completada ? 1 : -1;
      return (a.fechaProgramada || "").localeCompare(b.fechaProgramada || "");
    });

  return (
    <Box>
      <SectionHeader
        title="Seguimiento"
        description="Tareas programadas y leads que necesitan contacto."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenNueva(true)}>
            Nueva tarea
          </Button>
        }
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <KpiCard tone="pipeline" icon={<ScheduleIcon />} label="Para hoy" value={store.tareasHoy.length} helper="Tareas programadas" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="danger" icon={<WarningAmberIcon />} label="Vencidas" value={store.tareasVencidas.length} helper="Tareas atrasadas" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="warning" icon={<WarningAmberIcon />} label="Sin contacto" value={store.leadsSinSeguimiento.length} helper="Leads con 3+ días sin contacto" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="revenue" icon={<TrendingUpIcon />} label="Leads calientes" value={store.leadsConScore.filter((l) => l.score >= 40).length} helper="Score de 40 o más" />
        </Grid>
      </Grid>

      {/* Leads sin seguimiento */}
      {store.leadsSinSeguimiento.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" color="warning.main" mb={1}>
            Leads sin seguimiento (3+ días)
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {store.leadsSinSeguimiento.slice(0, 10).map((l) => (
              <Chip key={l.id} label={`${l.nombre} · ${canalLabel(l.canal)}`} color="warning" size="small" variant="outlined" sx={{ maxWidth: "100%" }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Leads calientes */}
      {store.leadsConScore.filter((l) => l.score >= 40).length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" color="success.main" mb={1}>
            Leads con alta probabilidad de compra
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {store.leadsConScore.filter((l) => l.score >= 40).slice(0, 5).map((l) => (
              <Chip key={l.id} label={`${l.nombre} · score ${l.score}`} color="success" variant="outlined" size="small" sx={{ maxWidth: "100%" }} />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Filtros */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>Estado</Typography>
        {[
          { value: "pendientes", label: "Pendientes" },
          { value: "vencidas", label: "Vencidas" },
          { value: "completadas", label: "Completadas" },
          { value: "canceladas", label: "Canceladas" },
          { value: "todas", label: "Todas" },
        ].map((f) => (
          <Chip
            key={f.value}
            label={f.label}
            onClick={() => setFiltro(f.value)}
            color={filtro === f.value ? "primary" : "default"}
            variant={filtro === f.value ? "filled" : "outlined"}
            size="small"
          />
        ))}
      </Box>

      {/* Filtro por tipo */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>Tipo</Typography>
        {[
          { value: "", label: "Todos" },
          { value: "visita", label: "Visitas" },
          { value: "seguimiento", label: "Seguimiento" },
          { value: "llamada", label: "Llamadas" },
        ].map((f) => (
          <Chip
            key={f.value}
            label={f.label}
            onClick={() => setFiltroTipo(f.value)}
            color={filtroTipo === f.value ? "primary" : "default"}
            variant={filtroTipo === f.value ? "filled" : "outlined"}
            size="small"
          />
        ))}
      </Box>

      {/* Form nueva tarea */}
      {openNueva && <NuevaTareaForm leads={store.leads} onAdd={store.agregarTarea} onClose={() => setOpenNueva(false)} />}

      {/* Lista de tareas */}
      <Stack spacing={1}>
        {tareas.map((t) => (
          <TareaItem
            key={t.id}
            tarea={t}
            leadNombre={getLeadNombre(t.leadId)}
            hoy={hoy}
            onCompletar={store.completarTarea}
            onEliminar={store.eliminarTarea}
          />
        ))}
        {tareas.length === 0 && (
          <Typography color="text.secondary" textAlign="center" py={3}>
            No hay tareas con estos filtros
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
